import type { CartLine } from '../../../gateways/breadstack/customer/behaviorCartCheckoutApis'
import { productName } from './behaviorProducts'
import { formatMoney2 } from './behaviorSearch'

// The Cart Recovery drawer's email in the Campaign Builder's block format, so
// the created campaign opens (and keeps editing) in the builder. A local
// mirror of the builder's block model (`Loyalty/marketing/builder/blocks/*`:
// `types.ts`, `makeBlock.ts`, `catalog.ts`, `starters.ts`, `compileEmail.ts`)
// limited to the blocks a recovery email uses — keep the shapes in sync with
// it. `compileRecoveryEmail` is the `compiled_html` the backend sends (body
// only; the backend wraps it) and, with target 'preview', the drawer's preview.

export type RecoveryBlockType =
  | 'header'
  | 'image'
  | 'text'
  | 'cart'
  | 'coupon'
  | 'button'
  | 'social'
  | 'footer'

export interface RecoveryBlockStyle {
  padT: number
  padB: number
  padX: number
  bg: string
}

export interface RecoveryBlock {
  id: string
  type: RecoveryBlockType
  props: Record<string, unknown>
  style: RecoveryBlockStyle
  cond: { mode: 'all'; rule: 'tier'; values: string[]; num: number }
}

export interface RecoveryEmailSettings {
  bg: string
  body: string
  width: number
  font: string
  text: string
  brand: string
}

// Builder `DEFAULT_BRAND` — the email's brand colour when the store has none.
export const DEFAULT_EMAIL_BRAND = '#1E7D4A'
const FONT = 'Helvetica, Arial, sans-serif'
const MUTED = '#6B7280'

const HEX6 = /^#[0-9a-fA-F]{6}$/
const HEX3 = /^#[0-9a-fA-F]{3}$/

// A #rrggbb colour (#rgb expanded), else ''. The server only accepts #rrggbb.
export const toHexColor = (v: string | null | undefined): string => {
  const s = (v ?? '').trim()
  if (HEX6.test(s)) return s.toUpperCase()
  if (HEX3.test(s))
    return `#${s[1]}${s[1]}${s[2]}${s[2]}${s[3]}${s[3]}`.toUpperCase()
  return ''
}

// Builder `defaultSettings(brand)`, already in the server's accepted shape.
export const recoveryEmailSettings = (
  brand: string,
): RecoveryEmailSettings => ({
  bg: '#F3F4F6',
  body: '#FFFFFF',
  width: 600,
  font: FONT,
  text: '#1F2937',
  brand: toHexColor(brand) || DEFAULT_EMAIL_BRAND,
})

// Builder `FOOTER_HTML`.
const FOOTER_HTML =
  '<strong>{{store_name}}</strong><br><br>You’re receiving this because you joined {{store_name}} Rewards or opted in at checkout.<br>For adults 19+ only. Please consume responsibly.'

// Builder `makeBlock` defaults for the block types used here.
const DEFAULTS: Record<
  RecoveryBlockType,
  { props: Record<string, unknown>; style: Partial<RecoveryBlockStyle> }
> = {
  header: {
    props: {
      logoText: '',
      logoHtml: '<b>{{store_name}}</b>',
      logoFont: '',
      logoSize: 22,
      logoColor: '',
      logoSpacing: '0.14em',
      logoImg: '',
      logoMode: 'auto',
      align: 'center',
      showLinks: true,
      links: [
        { label: 'Shop', url: '' },
        { label: 'Rewards', url: '' },
        { label: 'Store locator', url: '' },
      ],
      linkColor: '',
      linkSize: 13,
    },
    style: { padT: 22, padB: 16 },
  },
  image: {
    props: { src: '', alt: '', link: '', width: 100, radius: 0 },
    style: {},
  },
  text: {
    props: { preset: 'p', size: 15, html: '', color: '', align: 'left' },
    style: {},
  },
  cart: {
    props: {
      title: 'Still in your cart',
      showTitle: true,
      titlePreset: 'h2',
      titleSize: 22,
      titleColor: '',
      titleAlign: 'center',
      showImage: true,
      showMeta: true,
      showPrice: true,
    },
    style: {},
  },
  coupon: {
    props: {
      code: '',
      title: '',
      note: '',
      line: 'dashed',
      showScope: true,
      scopeText: '',
    },
    style: { padT: 16, padB: 16 },
  },
  button: {
    props: {
      label: 'Shop now',
      url: 'https://',
      bg: '',
      color: '#FFFFFF',
      radius: 6,
      size: 'md',
      full: false,
      align: 'center',
    },
    style: { padT: 16, padB: 16 },
  },
  social: {
    props: {
      nets: {
        instagram: true,
        facebook: true,
        x: true,
        tiktok: false,
        youtube: false,
      },
      urls: { instagram: '', facebook: '', x: '', tiktok: '', youtube: '' },
      align: 'center',
      shape: 'solid',
    },
    style: { padT: 16, padB: 8 },
  },
  footer: {
    props: { html: FOOTER_HTML, align: 'center', size: 12, color: MUTED },
    style: { padT: 24, padB: 28, bg: '#F9FAFB' },
  },
}

const BASE_STYLE: RecoveryBlockStyle = { padT: 12, padB: 12, padX: 24, bg: '' }

let counter = 0
const newBlockId = () => {
  counter = (counter + 1) % 1e6
  const rand = Math.random().toString(36).slice(2, 8)
  return `b${Date.now().toString(36)}${counter.toString(36)}${rand}`
}

const makeBlock = (
  type: RecoveryBlockType,
  props: Record<string, unknown> = {},
  style: Partial<RecoveryBlockStyle> = {},
): RecoveryBlock => ({
  id: newBlockId(),
  type,
  props: { ...JSON.parse(JSON.stringify(DEFAULTS[type].props)), ...props },
  style: { ...BASE_STYLE, ...DEFAULTS[type].style, ...style },
  cond: { mode: 'all', rule: 'tier', values: [], num: 1500 },
})

export const escapeHtml = (text: string) =>
  text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')

// Plain drawer text (already escaped / tokenised by the caller) → the text
// block's inline HTML: blank-line separated paragraphs, newlines as <br>.
export const textToBlockHtml = (escaped: string): string =>
  escaped
    .trim()
    .split(/\n\s*\n/)
    .map((p) => p.trim().replace(/\n/g, '<br>'))
    .filter(Boolean)
    .join('<br><br>')

export interface RecoveryBlocksInput {
  // Starter templates get the builder's hero image slot; blank doesn't.
  withHero: boolean
  storeName: string
  // Inline HTML (escaped, tokens as the target needs them).
  headlineHtml: string
  bodyHtml: string
  // '' = no coupon block.
  couponCode: string
  couponTitle: string
  couponNote: string
  ctaText: string
}

// The builder's Abandoned Cart starter (`starterBlocks`) with the drawer's
// copy: header → hero slot → headline → message → cart → coupon → CTA →
// social → footer.
export const buildRecoveryBlocks = (
  p: RecoveryBlocksInput,
): RecoveryBlock[] => {
  const store = escapeHtml(p.storeName.trim())
  const blocks: RecoveryBlock[] = [
    makeBlock('header', {
      logoHtml: store
        ? `<b>${store.toUpperCase()}</b>`
        : '<b>{{store_name}}</b>',
    }),
  ]
  if (p.withHero)
    blocks.push(
      makeBlock(
        'image',
        { src: '', alt: p.storeName || 'Hero image' },
        { padT: 0, padB: 0, padX: 0 },
      ),
    )
  blocks.push(
    makeBlock(
      'text',
      { preset: 'h1', size: 30, html: p.headlineHtml, align: 'center' },
      { padT: 28, padB: 6 },
    ),
    makeBlock(
      'text',
      { preset: 'p', size: 15, html: p.bodyHtml, align: 'center' },
      { padT: 6, padB: 12 },
    ),
    makeBlock('cart'),
  )
  if (p.couponCode)
    blocks.push(
      makeBlock('coupon', {
        code: p.couponCode,
        title: p.couponTitle,
        note: p.couponNote,
        // The builder keeps prefilled wording as-is instead of falling back to
        // the coupon's own title / description.
        prefilled: true,
      }),
    )
  blocks.push(
    makeBlock('button', { label: p.ctaText, url: '{{cart_url}}' }),
    makeBlock('social'),
    makeBlock('footer'),
  )
  return blocks
}

// ── Compile ─────────────────────────────────────────────────────────────────

export interface RecoveryCompileContext {
  // 'email' = `compiled_html` (per-recipient cart rows as `bs:cart_items`
  // markers, `{{tokens}}` kept). 'preview' = the drawer's preview: this
  // cart's lines, store name filled in, inert links.
  target: 'email' | 'preview'
  brandLogoUrl: string
  storeName: string
  storeUrl: string
  // Preview only.
  lines?: CartLine[]
}

const T =
  '<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">'

const httpsUrl = (url: string) =>
  /^https:\/\//i.test(url.trim()) ? escapeHtml(url.trim()) : ''

// Builder `shade` with a > 0: lighten toward white.
const tint = (hex: string, a: number) => {
  const n = Number.parseInt(hex.slice(1), 16)
  return `#${[n >> 16, (n >> 8) & 255, n & 255]
    .map((v) =>
      Math.round(v + (255 - v) * a)
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}`
}

const PRESETS: Record<string, { weight: number; lh: number }> = {
  h1: { weight: 800, lh: 1.2 },
  h2: { weight: 700, lh: 1.3 },
  p: { weight: 400, lh: 1.6 },
}

const str = (v: unknown) => (typeof v === 'string' ? v : '')
const num = (v: unknown, d: number) =>
  typeof v === 'number' && Number.isFinite(v) ? v : d

const buttonHtml = (label: string, href: string, bg: string) =>
  `${T}<tr><td align="center"><table role="presentation" cellpadding="0" cellspacing="0" border="0"><tr><td align="center" bgcolor="${bg}" style="background:${bg};border-radius:6px"><a href="${href}" style="display:inline-block;padding:13px 28px;font-size:15px;color:#FFFFFF;font-weight:700;text-decoration:none;text-align:center;line-height:1.2;border-radius:6px">${label}</a></td></tr></table></td></tr></table>`

type Renderer = (
  b: RecoveryBlock,
  s: RecoveryEmailSettings,
  x: RecoveryCompileContext,
) => string

const SOCIAL_GLYPHS: Array<[string, string, string]> = [
  ['instagram', 'IG', 'Instagram'],
  ['facebook', 'f', 'Facebook'],
  ['x', 'X', 'X'],
]

const link = (x: RecoveryCompileContext, emailHref: string) =>
  x.target === 'email' ? emailHref : '#'

const RENDER: Record<RecoveryBlockType, Renderer> = {
  header: (b, s, x) => {
    const p = b.props
    const logoUrl = httpsUrl(str(p.logoImg)) || httpsUrl(x.brandLogoUrl)
    const logo = logoUrl
      ? `<img src="${logoUrl}" alt="${escapeHtml(x.storeName)}" style="max-height:44px;max-width:200px;display:inline-block;border:0">`
      : `<div style="font-family:${s.font};font-size:${num(p.logoSize, 22)}px;line-height:1.2;letter-spacing:${str(p.logoSpacing) || '0'};color:${s.brand}">${str(p.logoHtml)}</div>`
    const links = Array.isArray(p.links)
      ? (p.links as Array<{ label?: string }>).filter((l) => l?.label?.trim())
      : []
    const storeHref = httpsUrl(x.storeUrl) || '#'
    const nav =
      p.showLinks && links.length
        ? `<div style="margin-top:12px;font-size:${num(p.linkSize, 13)}px;line-height:1.8">${links
            .map(
              (l) =>
                `<a href="${link(x, storeHref)}" style="color:${s.text};text-decoration:none;margin:0 9px;font-weight:500;white-space:nowrap">${escapeHtml(l.label ?? '')}</a>`,
            )
            .join('')}</div>`
        : ''
    return `<div style="text-align:center">${logo}${nav}</div>`
  },
  // The hero slot has no image until one is set in the builder.
  image: (b) => {
    const src = httpsUrl(str(b.props.src))
    return src
      ? `<div style="text-align:center;line-height:0;font-size:0"><img src="${src}" alt="${escapeHtml(str(b.props.alt))}" style="display:inline-block;width:100%;max-width:100%;height:auto;vertical-align:top;border:0"></div>`
      : ''
  },
  text: (b, s) => {
    const p = b.props
    const pr = PRESETS[str(p.preset)] ?? PRESETS.p
    return `<div style="margin:0;font-size:${num(p.size, 15)}px;font-weight:${pr.weight};line-height:${pr.lh};color:${s.text};text-align:${str(p.align) || 'left'}">${str(p.html)}</div>`
  },
  cart: (b, s, x) => {
    const p = b.props
    const title = p.showTitle
      ? `<div style="margin:0 0 14px;font-size:${num(p.titleSize, 22)}px;font-weight:700;line-height:1.3;color:${s.text};text-align:center">${escapeHtml(str(p.title))}</div>`
      : ''
    const row = (img: string, name: string, meta: string, price: string) =>
      `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="border:1px solid #E5E7EB;border-radius:10px;background:#FFFFFF;border-collapse:separate;margin-bottom:10px"><tr><td width="74" valign="middle" style="width:74px;padding:10px 0 10px 10px">${img}</td><td valign="middle" style="padding:10px 12px"><div style="font-size:14px;font-weight:700;color:${s.text};line-height:1.3">${name}</div>${meta}</td><td align="right" valign="middle" style="padding:10px 12px;font-size:15px;font-weight:800;color:${s.text};white-space:nowrap;text-align:right">${price}</td></tr></table>`
    const fallback = buttonHtml(
      'View your cart',
      link(x, '{{cart_url}}'),
      s.brand,
    )
    if (x.target === 'email')
      // The backend repeats the row per cart line, else shows the fallback.
      return `${title}<!--bs:cart_items-->${row(
        '<img src="{{item_image}}" alt="{{item_name}}" width="64" style="display:block;width:64px;height:auto;border:0;border-radius:8px">',
        '{{item_name}}',
        `<div style="font-size:12px;color:${MUTED};margin-top:3px">{{item_variant}}</div><div style="font-size:12px;color:${MUTED};margin-top:2px">Qty {{item_qty}}</div>`,
        '{{item_price}}',
      )}<!--bs:cart_fallback-->${fallback}<!--bs:/cart_items-->`
    const lines = x.lines ?? []
    if (!lines.length) return title + fallback
    return (
      title +
      lines
        .map((l) => {
          const name = escapeHtml(productName(l))
          const src = httpsUrl(l.imageUrl ?? '')
          const img = src
            ? `<img src="${src}" alt="${name}" width="64" style="display:block;width:64px;height:auto;border:0;border-radius:8px">`
            : '<div style="width:64px;height:64px;background:#F3F4F6;border-radius:8px">&nbsp;</div>'
          return row(
            img,
            name,
            `<div style="font-size:12px;color:${MUTED};margin-top:2px">Qty ${l.quantity}</div>`,
            escapeHtml(formatMoney2(l.quantity * l.price)),
          )
        })
        .join('')
    )
  },
  coupon: (b, s) => {
    const p = b.props
    const code = str(p.code).trim()
    if (!code) return ''
    const title = str(p.title).trim()
    const note = str(p.note).trim()
    return `${T}<tr><td align="center" style="border:2px dashed ${s.brand};border-radius:12px;background:${tint(s.brand, 0.93)};padding:18px 20px;text-align:center">${
      title
        ? `<div style="font-size:15px;font-weight:700;color:${s.text}">${escapeHtml(title)}</div>`
        : ''
    }<div style="font-family:'Courier New',Courier,monospace;font-size:26px;font-weight:800;letter-spacing:.14em;color:${s.brand};margin:8px 0 6px">${escapeHtml(code)}</div>${
      note
        ? `<div style="font-size:12px;color:${MUTED}">${escapeHtml(note)}</div>`
        : ''
    }</td></tr></table>`
  },
  button: (b, s, x) =>
    buttonHtml(
      escapeHtml(str(b.props.label)),
      link(x, escapeHtml(str(b.props.url))),
      s.brand,
    ),
  // Builder `socialBlock`: a network with no link is left out of the email
  // (and so of the preview) — the drawer sets none, so this renders nothing
  // until links are added in the builder.
  social: (b, s, x) => {
    const nets = (b.props.nets ?? {}) as Record<string, unknown>
    const urls = (b.props.urls ?? {}) as Record<string, unknown>
    const icon = (href: string, glyph: string, title: string) =>
      `<a href="${link(x, href)}" title="${title}" style="display:inline-block;margin:0 4px;text-align:center;font-size:12px;font-weight:800;text-decoration:none;border-radius:50%;width:34px;height:34px;line-height:34px;background:${s.brand};color:#FFFFFF">${glyph}</a>`
    const icons = SOCIAL_GLYPHS.filter(
      ([k]) => nets[k] && httpsUrl(str(urls[k])),
    )
      .map(([k, glyph, title]) => icon(httpsUrl(str(urls[k])), glyph, title))
      .join('')
    return icons ? `<div style="text-align:center">${icons}</div>` : ''
  },
  footer: (b, _s, x) => {
    const html =
      x.target === 'email'
        ? str(b.props.html)
        : str(b.props.html).replace(
            /\{\{store_name\}\}/g,
            escapeHtml(x.storeName),
          )
    const st = `font-size:12px;line-height:1.6;color:${MUTED};text-align:center`
    return `<div style="${st}">${html}</div><div style="${st};margin-top:12px"><a href="${link(x, '{{unsubscribe_url}}')}" style="color:${MUTED};text-decoration:underline">Unsubscribe</a></div>`
  },
}

// Builder `compileEmail`: body HTML only, one padded table per block.
export const compileRecoveryEmail = (
  blocks: RecoveryBlock[],
  settings: RecoveryEmailSettings,
  ctx: RecoveryCompileContext,
): string =>
  blocks
    .map((b) => {
      const inner = RENDER[b.type](b, settings, ctx)
      if (!inner) return ''
      const { padT, padB, padX, bg } = b.style
      return `${T}<tr><td style="padding:${padT}px ${padX}px ${padB}px;background:${bg || 'transparent'}">${inner}</td></tr></table>`
    })
    .join('')

// Builder `wrapPreviewDocument` (the wrapper the backend applies), locked
// down for the drawer's sandboxed iframe: no scripts / remote styles, links
// and buttons inert.
export const wrapRecoveryPreview = (
  bodyHtml: string,
  settings: RecoveryEmailSettings,
  preheader: string,
): string =>
  `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="default-src 'none'; style-src 'unsafe-inline'; img-src data: https:;"><meta name="viewport" content="width=device-width,initial-scale=1"><style>a,area,form,button,input,select,textarea{pointer-events:none!important;cursor:default!important;}</style></head><body style="margin:0;padding:0;background:${settings.bg}"><div style="display:none;max-height:0;overflow:hidden">${escapeHtml(preheader)}</div><table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:${settings.bg}"><tr><td align="center" style="padding:24px 12px"><table role="presentation" width="${settings.width}" cellpadding="0" cellspacing="0" style="width:100%;max-width:${settings.width}px;background:${settings.body};font-family:${settings.font};color:${settings.text}"><tr><td>${bodyHtml}</td></tr></table></td></tr></table></body></html>`
